import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isSessionExpired } from "@/lib/session-expiration";

const TABLET_SESSION_KEY = "tablet_session";

export interface TabletSession {
  waiterSessionId: string;
  tableNumber: string;
  operatorName: string; // Fixed as "Tablet"
  setupAt: string;
  restaurantId: string;
}

export function useTabletSession() {
  const [session, setSession] = useState<TabletSession | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(TABLET_SESSION_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as TabletSession;
        // Check if session is from today
        const setupDate = new Date(parsed.setupAt).toDateString();
        const today = new Date().toDateString();
        if (setupDate === today) {
          setSession(parsed);
          
          // Immediately validate against database
          supabase
            .from("waiter_work_sessions")
            .select("status, expires_at")
            .eq("id", parsed.waiterSessionId)
            .single()
            .then(({ data, error }) => {
              if (error || !data || data.status !== "active") {
                console.log("[TabletSession] Session invalid on load:", data?.status);
                localStorage.removeItem(TABLET_SESSION_KEY);
                setSession(null);
                return;
              }
              // Check expiration
              if (data.expires_at && new Date(data.expires_at) < new Date()) {
                console.log("[TabletSession] Session expired on load:", data.expires_at);
                localStorage.removeItem(TABLET_SESSION_KEY);
                setSession(null);
              }
            });
        } else {
          // Clear expired session
          localStorage.removeItem(TABLET_SESSION_KEY);
        }
      } catch {
        localStorage.removeItem(TABLET_SESSION_KEY);
      }
    }
  }, []);

  const setTabletSession = useCallback((newSession: TabletSession) => {
    localStorage.setItem(TABLET_SESSION_KEY, JSON.stringify(newSession));
    setSession(newSession);
  }, []);

  const updateTableNumber = useCallback((tableNumber: string) => {
    if (session) {
      const updated = { ...session, tableNumber };
      localStorage.setItem(TABLET_SESSION_KEY, JSON.stringify(updated));
      setSession(updated);
    }
  }, [session]);

  const clearTabletSession = useCallback(() => {
    localStorage.removeItem(TABLET_SESSION_KEY);
    setSession(null);
  }, []);

  const isSessionValid = useCallback(() => {
    if (!session) return false;
    // Use midnight+6h rule: if session was set up before today and it's past 06:00 Brasilia, invalid
    if (isSessionExpired(null, session.setupAt)) {
      return false;
    }
    const setupDate = new Date(session.setupAt).toDateString();
    const today = new Date().toDateString();
    return setupDate === today;
  }, [session]);

  // Validate session status against database (including expiration)
  const validateSessionStatus = useCallback(async (sessionIdOverride?: string): Promise<boolean> => {
    const waiterSessionId = sessionIdOverride || session?.waiterSessionId;
    if (!waiterSessionId) return false;

    try {
      const { data, error } = await supabase
        .from("waiter_work_sessions")
        .select("status, expires_at")
        .eq("id", waiterSessionId)
        .single();

      if (error || !data || data.status !== "active") {
        console.log("[TabletSession] Session invalid:", data?.status);
        clearTabletSession();
        return false;
      }
      
      // Check if session has expired
      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        console.log("[TabletSession] Session expired at:", data.expires_at);
        clearTabletSession();
        return false;
      }
      
      return true;
    } catch {
      return false;
    }
  }, [session, clearTabletSession]);

  return {
    session,
    setTabletSession,
    updateTableNumber,
    clearTabletSession,
    isSessionValid,
    validateSessionStatus,
    hasSession: !!session && isSessionValid(),
  };
}
